import React, { useCallback, useEffect, useState } from 'react';
import { FolderOpen, Loader2, RefreshCw } from 'lucide-react';
import toast from 'react-hot-toast';
import api from '../../api';
import EmptyState from '../EmptyState';

/**
 * BD-17 — the expertise library: what TSG knows and has done, tagged and de-duplicated, each item marked either
 * client-shareable or internal-only. Everyone on BD can read it and try a match; only an admin can re-index it
 * from the Company Brain or change an item.
 *
 * Every item is INTERNAL-ONLY until an admin says otherwise, and only client-shareable items can ever be suggested
 * for anything that leaves the firm. "Try a match" asks for an audience and defaults to External, so what you see
 * there is what a client-facing draft would be offered.
 */

interface Item {
  id: number;
  source_type: string;
  title: string;
  summary: string;
  industries: string[];
  functions: string[];
  topics: string[];
  roles: string[];
  shareable: boolean;
  status: 'active' | 'duplicate' | 'retired';
  dup_of_id: number | null;
}
interface Result { id: number; kind: string; title: string; why: string; shareable: boolean; source: { label: string }; summary: string }

const KIND_LABEL: Record<string, string> = { case_study: 'Case study', bio: 'Team bio', capability: 'Jarvis capability', document: 'Document', manual: 'Library item' };

type TagKey = 'industries' | 'functions' | 'topics' | 'roles';
const TAG_LABEL: Record<TagKey, string> = { industries: 'Industries', functions: 'Functions', topics: 'Topics', roles: 'Roles' };
const TAG_KEYS: TagKey[] = ['industries', 'functions', 'topics', 'roles'];
const splitTags = (s: string) => s.split(',').map((x) => x.trim()).filter(Boolean);

const ExpertiseTab: React.FC = () => {
  const [editing, setEditing] = useState<number | null>(null);
  const [draft, setDraft] = useState<Record<TagKey, string>>({ industries: '', functions: '', topics: '', roles: '' });
  const [items, setItems] = useState<Item[]>([]);
  const [canEdit, setCanEdit] = useState(false);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState<number | 'reindex' | null>(null);
  const [topics, setTopics] = useState('');
  const [role, setRole] = useState('');
  const [industry, setIndustry] = useState('');
  const [audience, setAudience] = useState<'external' | 'internal'>('external');
  const [results, setResults] = useState<Result[] | null>(null);

  const load = useCallback(async () => {
    try {
      const res = await api.get('/api/bd/expertise');
      setItems(res.data.items || []);
      setCanEdit(!!res.data.can_edit);
    } catch {
      toast.error('Could not load the library');
    } finally {
      setLoading(false);
    }
  }, []);
  useEffect(() => { load(); }, [load]);

  const reindex = async () => {
    setBusy('reindex');
    try {
      const res = await api.post('/api/bd/expertise/reindex');
      toast.success(`Indexed: ${res.data.added} added, ${res.data.updated} updated, ${res.data.retired} retired`);
      await load();
    } catch (err: any) {
      toast.error(err?.response?.data?.error || 'Could not index the Company Brain');
    } finally {
      setBusy(null);
    }
  };

  const setShareable = async (item: Item, shareable: boolean) => {
    if (shareable && !window.confirm(`Mark "${item.title}" as client-shareable? It can then be suggested in drafts and notes that go to clients.`)) return;
    setBusy(item.id);
    try {
      await api.patch(`/api/bd/expertise/items/${item.id}`, { shareable });
      toast.success(shareable ? 'Now client-shareable' : 'Now internal-only');
      await load();
    } catch (err: any) {
      toast.error(err?.response?.data?.error || 'Could not save that');
    } finally {
      setBusy(null);
    }
  };

  const startEdit = (item: Item) => {
    setEditing(item.id);
    setDraft({ industries: item.industries.join(', '), functions: item.functions.join(', '), topics: item.topics.join(', '), roles: item.roles.join(', ') });
  };

  // Fills the boxes only. Nothing is saved until the admin presses Save tags.
  const suggestTags = async (item: Item) => {
    setBusy(item.id);
    try {
      const res = await api.post(`/api/bd/expertise/items/${item.id}/suggest-tags`);
      const sug = res.data.suggestion || {};
      setDraft({
        industries: (sug.industries || []).join(', '), functions: (sug.functions || []).join(', '),
        topics: (sug.topics || []).join(', '), roles: (sug.roles || []).join(', '),
      });
      toast.success('Suggestion filled in. Check it, then press Save tags.');
    } catch (err: any) {
      toast.error(err?.response?.data?.error || 'Could not suggest tags');
    } finally {
      setBusy(null);
    }
  };

  const saveTags = async (item: Item) => {
    setBusy(item.id);
    try {
      await api.patch(`/api/bd/expertise/items/${item.id}`, {
        industries: splitTags(draft.industries), functions: splitTags(draft.functions), topics: splitTags(draft.topics), roles: splitTags(draft.roles),
      });
      toast.success('Tags saved');
      setEditing(null);
      await load();
    } catch (err: any) {
      toast.error(err?.response?.data?.error || 'Could not save the tags');
    } finally {
      setBusy(null);
    }
  };

  const tryMatch = async () => {
    try {
      const list = (s: string) => s.split(',').map((x) => x.trim()).filter(Boolean);
      const res = await api.post('/api/bd/expertise/match', { context: { topics: list(topics), role: role.trim() || undefined, industry: industry.trim() || undefined }, audience });
      setResults(res.data.results || []);
    } catch {
      toast.error('Could not run the match');
    }
  };

  if (loading) return <div className="flex justify-center py-16"><Loader2 className="w-6 h-6 animate-spin text-gray-400" /></div>;

  const active = items.filter((i) => i.status === 'active');
  const collapsed = items.filter((i) => i.status !== 'active');
  const titleOf = (id: number | null) => items.find((i) => i.id === id)?.title || 'another item';

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-lg font-semibold">Expertise library</h2>
          <p className="text-sm text-gray-500">What TSG knows and has done. Only client-shareable items are ever suggested outside the firm.</p>
        </div>
        {canEdit && (
          <button type="button" disabled={busy === 'reindex'} onClick={reindex} className="inline-flex items-center gap-1 px-3 py-1.5 text-sm rounded border border-gray-300">
            {busy === 'reindex' ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />} Index the Company Brain
          </button>
        )}
      </div>

      {items.length === 0 ? (
        <EmptyState icon={FolderOpen} title="The library is empty" description={canEdit ? 'Index the Company Brain to add its case studies, bios and capabilities. Everything starts as internal-only.' : 'An admin needs to index the Company Brain first.'} />
      ) : (
        <ul className="divide-y divide-gray-100 dark:divide-gray-700 border border-gray-200 dark:border-gray-700 rounded-lg" data-testid="expertise-list">
          {active.map((it) => (
            <li key={it.id} className="p-3 flex items-start justify-between gap-3" data-testid={`expertise-item-${it.id}`}>
              <div>
                <p className="font-medium text-gray-900 dark:text-gray-100">{it.title} <span className="ml-2 text-xs text-gray-500">{KIND_LABEL[it.source_type] || it.source_type}</span></p>
                <p className="text-sm text-gray-600 dark:text-gray-300 line-clamp-2">{it.summary}</p>
                <p className="mt-1 text-xs text-gray-500">{[...it.industries, ...it.topics].slice(0, 8).join(' · ')}</p>
                {editing === it.id && (
                  <div className="mt-2 space-y-2 border border-gray-200 dark:border-gray-700 rounded p-3" data-testid={`expertise-edit-${it.id}`}>
                    {TAG_KEYS.map((k) => (
                      <label key={k} className="block text-xs text-gray-600">
                        {TAG_LABEL[k]} (comma separated)
                        <input aria-label={`${TAG_LABEL[k]} of ${it.title}`} className="mt-0.5 w-full border rounded px-2 py-1 text-sm" value={draft[k]} onChange={(e) => setDraft({ ...draft, [k]: e.target.value })} />
                      </label>
                    ))}
                    <div className="flex gap-2">
                      <button type="button" disabled={busy === it.id} onClick={() => saveTags(it)} className="px-3 py-1 text-sm rounded bg-blue-600 text-white">Save tags</button>
                      <button type="button" disabled={busy === it.id} onClick={() => suggestTags(it)} className="px-3 py-1 text-sm rounded border border-gray-300">Suggest tags</button>
                      <button type="button" onClick={() => setEditing(null)} className="px-3 py-1 text-sm rounded border border-gray-300">Cancel</button>
                    </div>
                  </div>
                )}
              </div>
              <div className="shrink-0 text-right">
                <span className={`inline-block text-xs rounded px-2 py-0.5 ${it.shareable ? 'bg-green-50 text-green-700' : 'bg-gray-100 text-gray-600'}`}>{it.shareable ? 'Client-shareable' : 'Internal only'}</span>
                {canEdit && (
                  <div className="mt-1">
                    <button type="button" disabled={busy === it.id} onClick={() => setShareable(it, !it.shareable)} className="text-xs text-blue-600 hover:underline">
                      {it.shareable ? 'Make internal-only' : 'Make client-shareable'}
                    </button>
                    <button type="button" onClick={() => (editing === it.id ? setEditing(null) : startEdit(it))} className="block ml-auto text-xs text-blue-600 hover:underline">Edit tags</button>
                  </div>
                )}
              </div>
            </li>
          ))}
        </ul>
      )}

      {collapsed.length > 0 && (
        <details className="text-sm text-gray-600">
          <summary className="cursor-pointer">{collapsed.length} item{collapsed.length === 1 ? '' : 's'} collapsed or retired</summary>
          <ul className="mt-2 space-y-1">
            {collapsed.map((it) => (
              <li key={it.id}>{it.title}: {it.status === 'duplicate' ? `a duplicate of ${titleOf(it.dup_of_id)}` : 'retired'}</li>
            ))}
          </ul>
        </details>
      )}

      <section className="border border-gray-200 dark:border-gray-700 rounded-lg p-4">
        <h3 className="font-semibold">Try a match</h3>
        <p className="text-sm text-gray-500">Describe a person or account and see which items would be suggested.</p>
        <div className="mt-3 grid gap-2 sm:grid-cols-2">
          <input aria-label="Topics" className="border rounded px-2 py-1 text-sm" placeholder="Topics, comma separated (operational risk, RCSA)" value={topics} onChange={(e) => setTopics(e.target.value)} />
          <input aria-label="Role" className="border rounded px-2 py-1 text-sm" placeholder="Their role (Chief Risk Officer)" value={role} onChange={(e) => setRole(e.target.value)} />
          <input aria-label="Industry" className="border rounded px-2 py-1 text-sm" placeholder="Industry (Insurance)" value={industry} onChange={(e) => setIndustry(e.target.value)} />
          <select aria-label="Audience" className="border rounded px-2 py-1 text-sm" value={audience} onChange={(e) => setAudience(e.target.value as 'external' | 'internal')}>
            <option value="external">External: a client-facing draft</option>
            <option value="internal">Internal: a brief or prep</option>
          </select>
        </div>
        <button type="button" onClick={tryMatch} className="mt-3 px-3 py-1.5 text-sm rounded bg-blue-600 text-white">Find matches</button>
        {results && (
          results.length === 0 ? (
            <p className="mt-3 text-sm text-gray-500" data-testid="expertise-no-match">Nothing fits{audience === 'external' ? ' (only client-shareable items are considered)' : ''}.</p>
          ) : (
            <ul className="mt-3 space-y-2" data-testid="expertise-results">
              {results.map((r) => (
                <li key={r.id} className="text-sm">
                  <span className="font-medium">{r.title}</span> <span className="text-xs text-gray-500">({r.source.label}{r.shareable ? ', client-shareable' : ', internal only'})</span>
                  <p className="text-gray-600 dark:text-gray-300">{r.why}</p>
                </li>
              ))}
            </ul>
          )
        )}
      </section>
    </div>
  );
};

export default ExpertiseTab;
